import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getStorage, setStorage, clearStorage } from "../utils/localStorage";

export const apiBaseUrl = import.meta.env.VITE_API_URL || "https://stacked-backend-gv43.onrender.com/api/v1";

export const baseQuery = fetchBaseQuery({
  baseUrl: apiBaseUrl,
  prepareHeaders: (headers) => {
    const adminToken = getStorage("__admin_token__");
    const userToken = getStorage("__login_user_token__");
    const token = adminToken || userToken;

    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

// Prevent multiple simultaneous refresh attempts
let refreshPromise = null;

async function doRefresh() {
  const refreshToken = getStorage("__refresh_token__");
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (data?.success && data?.data?.token && data?.data?.refreshToken) {
      // Store the new token pair
      setStorage("__login_user_token__", data.data.token);
      setStorage("__refresh_token__", data.data.refreshToken);
      return data.data.token;
    }
    return null;
  } catch {
    return null;
  }
}

// Enhanced baseQuery with automatic token refresh on 401
export const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  
  // Handle 429 Too Many Requests — do NOT logout, just let the error propagate
  // The UI will show an error or retry automatically
  if (result.error && result.error.status === 429) {
    console.warn("[API] Rate limited (429) — request will be retried by RTK Query or UI");
    return result;
  }

  // Handle 401 Unauthorized — try refreshing the token first
  if (result.error && result.error.status === 401) {
    // Deduplicate: if a refresh is already in progress, wait for it
    if (!refreshPromise) {
      refreshPromise = doRefresh().finally(() => { refreshPromise = null; });
    }

    const newToken = await refreshPromise;

    if (newToken) {
      // Retry the original request with the new token
      result = await baseQuery(args, api, extraOptions);
    } else {
      // Refresh failed — clear EVERYTHING in localStorage (not just the
      // known auth keys) so leftover impersonation state, admin token
      // backups, dismissed-onboarding flags, etc. all reset cleanly for
      // the next user.
      clearStorage();

      const errorMessage = result.error.data?.message || "Your session has expired. Please login again.";
      
      if (window.toast && window.toast.error) {
        window.toast.error(errorMessage);
      }
      
      setTimeout(() => {
        window.location.href = "/login";
      }, 1000);
    }
  }

  // Handle plan-related 403s. The backend returns structured codes for
  // PLAN_LEVEL_REQUIRED, CATEGORY_REQUIRED, CATEGORY_MISMATCH, ADDON_REQUIRED.
  // Surface a clear toast pointing to /pricing rather than letting the
  // user see a blank page or a generic error.
  if (result.error && result.error.status === 403) {
    const code = result.error.data?.code;
    const message = result.error.data?.message;
    if (code === "PLAN_LEVEL_REQUIRED" || code === "CATEGORY_REQUIRED" ||
        code === "CATEGORY_MISMATCH" || code === "ADDON_REQUIRED") {
      if (window.toast && window.toast.error) {
        window.toast.error(message || "Your plan doesn't include this feature.");
      }
      // Components can also inspect the error code from RTK Query's
      // result.error to render a CTA. We don't auto-redirect because
      // the user may be mid-flow.
    }
  }

  return result;
};
